// The bullets in an agent's opening message ("Here's what I can help
// with"), keyed by the gallery template's stable `key`. An agent made from
// scratch has no list: its opening message points at its own instructions.
const TEMPLATE_CAPABILITIES: Record<string, readonly string[]> = {
  assignment_janitor: [
    "Spot people assigned training that doesn't fit their role",
    "Find required assignments missing for a location",
    "Flag duplicate or over-assigned content",
  ],
  certification_watchdog: [
    "List certifications expiring soon, by location",
    "Find people working with an expired cert",
    "Plan who to reassign before a deadline",
  ],
  journey_debugger: [
    "Walk through one person's training, step by step",
    "Show where someone stalled and why",
    "Spot patterns in where new hires get stuck",
  ],
  onboarding_coach: [
    "Plan what to assign new hires, and in what order",
    "Find new hires falling behind",
    "Check that onboarding actually landed",
  ],
  role_change_coach: [
    "Work out what's due after a promotion or transfer",
    "Separate what's already done from what's new",
    "Suggest the assignments to add",
  ],
  store_opening_coordinator: [
    "Build a training plan for a new location",
    "Track who at the new store has started",
    "Keep the opening timeline on track",
  ],
  content_writer: [
    "Draft modules, courses and check-ins",
    "Match your org's existing tone",
    "Turn procedures into training",
  ],
  content_optimization_expert: [
    "Find the quiz questions people miss most",
    "Explain why a lesson isn't landing",
    "Recommend fixes for low-scoring content",
  ],
  stale_content_hunter: [
    "Find content that's unused or out of date",
    "List drafts that were never published",
    "Suggest what to update, merge or retire",
  ],
  tone_consistency_editor: [
    "Check new content against your voice",
    "Keep reading level consistent",
    "Rewrite passages to match your brand",
  ],
  translation_qa: [
    "Find published content missing a language",
    "Spot translations out of date with the original",
    "Flag mismatched or partial locales",
  ],
  audit_prep_brief: [
    "Prepare a one-pager before a location visit",
    "Summarize compliance by location or role",
    "Highlight what to fix before an audit",
  ],
  compliance_auditor: [
    "See who is overdue, by location and role",
    "Find your biggest compliance gaps",
    "Plan how to close them",
  ],
  guest_feedback_trainer: [
    "Summarize complaint themes from guest feedback",
    "Match themes to the training that covers them",
    "Suggest who should retake what",
  ],
  reporting_analyst: [
    "Answer data questions with real numbers",
    "Compare locations, roles and time periods",
    "Explain exactly what each number counts",
  ],
  task_checklist_analyst: [
    "Find the checklists that fail most, and where",
    "Tell training gaps from process gaps",
    "Suggest fixes for missed tasks",
  ],
  automation_advisor: [
    "Find manual assigning that could be automated",
    "Suggest automations for new hires and role changes",
    "Explain what an automation will do before you set it up",
  ],
  opus_optimization_expert: [
    "Show Opus features you aren't using yet",
    "Get more out of your existing content",
    "Suggest setup changes that save time",
  ],
  reporting_recipe_builder: [
    "Turn a report request into filters and grouping",
    "Set up reports on a schedule",
    "Explain how to read the results",
  ],
};

export const agentCapabilities = (
  templateKey: string | null | undefined,
): readonly string[] =>
  (templateKey && TEMPLATE_CAPABILITIES[templateKey]) || [];

// Ask Opus's own list; drafting only where this admin can build.
export const askOpusCapabilities = (canBuild: boolean): readonly string[] => [
  "Answer questions about your team's training and progress",
  "Find anything in your library",
  ...(canBuild ? ["Draft modules, checklists and check-ins"] : []),
];
